import type { FC } from 'react';
import { useMemo } from 'react';
import { Box, Flex, VStack, useDisclosure } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import type { SubmitHandler } from 'react-hook-form';
import { useCustomerResetPassword } from '../../../../bff-api/customers/useCustomerResetPassword';
import { Field } from '../../../../components/field';
import { useNavigation, useSearchParams } from '../../../../hooks';
import { Button, Header, Text } from '../../../../ui-components';
import { PageUrl } from '../../../utils/page-url';
import { PasswordRequirements } from '../password-requirements/PasswordRequirements';
import { ResetPasswordErrorModal } from './reset-password-error-modal/ResetPasswordErrorModal';
import { useResetPasswordFields } from './useResetPasswordFields';
import type { LoginLocationState } from '../login/Login';
import type { ResetPasswordFormValues } from './useResetPasswordFields';

export const ResetPassword: FC = () => {
    const { isLoading, mutate } = useCustomerResetPassword();
    const navigate = useNavigation<LoginLocationState>();
    const { isOpen, onClose, onOpen } = useDisclosure();
    const form = useForm<ResetPasswordFormValues>();
    const fields = useResetPasswordFields(form);
    const password = form.watch('password');
    const query = useSearchParams();

    const token = query.get('token') ?? '';
    const email = decodeURIComponent(query.get('email') ?? '');

    const queryParamsAreValid = useMemo(() => !!token && !!email, [token, email]);

    const onSubmit: SubmitHandler<ResetPasswordFormValues> = (values) => {
        mutate(
            { email, resetToken: token, newPassword: values.password },
            {
                onSuccess: () => {
                    navigate(PageUrl.ACCOUNT_LOGIN, 'replace', { successfulPasswordReset: true });
                },
                onError: () => {
                    onOpen();
                },
            },
        );
    };

    return (
        <div
            className="flex h-full w-full justify-center"
            data-testid="account-reset-password-main-container"
        >
            <Flex
                width="100%"
                maxWidth={{ base: '100%', md: '680px' }}
                padding={0}
                py={{ base: 4, sm: 8 }}
            >
                <VStack spacing={4} width="100%" padding={0}>
                    <Flex px={{ base: 4, sm: 8, md: 0 }} justifyContent="start" width="100%">
                        <Header
                            as="h3"
                            variant="header3"
                            id="account-reset-password-title"
                            style="bold"
                            color="slt-pepper.1000"
                        >
                            <FormattedMessage
                                id="account.reset-password.title"
                                defaultMessage="Reset your password"
                            />
                        </Header>
                    </Flex>

                    <Box
                        width="100%"
                        padding={{ base: 4, sm: 8, md: 4 }}
                        bg="white"
                        borderRadius={{ md: 'lg' }}
                        boxShadow="0px 2px 10px 0px rgba(65, 64, 66, 0.14)"
                    >
                        <VStack spacing={6} width="100%">
                            <VStack spacing={1} width="100%" align="stretch">
                                <Text style="bold" id="account-reset-password-email-label">
                                    <FormattedMessage
                                        defaultMessage="Email address:"
                                        id="account.reset-password.email_address"
                                    />
                                </Text>
                                <Text id="account-reset-password-email-value">{email}</Text>
                            </VStack>

                            <form
                                onSubmit={form.handleSubmit(onSubmit)}
                                data-testid="account-reset-password-form"
                                noValidate
                                className="w-full"
                            >
                                <VStack spacing={6} width="100%">
                                    <VStack spacing={2} width="100%" align="stretch">
                                        <Field {...fields.password} />
                                        <Text
                                            size="small"
                                            style="bold"
                                            id="account-reset-password-requirements-label"
                                        >
                                            <FormattedMessage
                                                defaultMessage="Password must contain the following:"
                                                id="account.reset-password.password_requirements"
                                            />
                                        </Text>
                                        <PasswordRequirements value={password ?? ''} />
                                    </VStack>

                                    <Box width="100%" display="flex" justifyContent="center">
                                        <Button
                                            id="account-reset-password-submit-button"
                                            kind="submit"
                                            variant="primary-black"
                                            isDisabled={
                                                form.formState.isSubmitting ||
                                                !queryParamsAreValid ||
                                                isLoading
                                            }
                                            isLoading={isLoading}
                                        >
                                            <FormattedMessage
                                                defaultMessage="Reset Password"
                                                id="account.reset-password.button.reset_password"
                                            />
                                        </Button>
                                    </Box>
                                </VStack>
                            </form>
                        </VStack>
                    </Box>
                </VStack>

                <ResetPasswordErrorModal isOpen={isOpen} onClose={onClose} />
            </Flex>
        </div>
    );
};
